import axios from 'axios';

// AppBridge 类型定义
interface AppBridgeInstance {
  getSessionToken: () => Promise<string>;
  invoke: (method: string, params: any) => Promise<any>;
}

// 获取 AppBridge 实例的辅助函数
let appBridgeInstance: AppBridgeInstance | null = null;

export function setAppBridgeInstance(bridge: AppBridgeInstance | null) {
  appBridgeInstance = bridge;
}

// API 基础 URL - 使用相对路径，因为前端通过 Worker 代理访问
// Worker 会将请求转发到后端 API
const api = axios.create({
  baseURL: '', // 使用相对路径，请求会发送到当前域名（Worker 自定义域名）
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加 SessionToken
api.interceptors.request.use(
  async (config) => {
    // 通过 AppBridge 获取 sessionToken
    if (appBridgeInstance) {
      try {
        const sessionToken = await appBridgeInstance.getSessionToken();
        if (sessionToken) {
          config.headers.Authorization = `Bearer ${sessionToken}`;
        }
      } catch (error) {
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 统一处理错误
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // 优先使用后端返回的错误信息
    const errorData = error.response?.data;
    
    if (errorData) {
      // 如果后端返回了错误信息，包装成标准错误对象
      const enhancedError = new Error(errorData.error || errorData.message || '请求失败');
      (enhancedError as any).code = errorData.code;
      (enhancedError as any).data = errorData.data;
      (enhancedError as any).status = error.response?.status;
      return Promise.reject(enhancedError);
    }
    
    // 如果没有错误数据，使用默认错误
    return Promise.reject(error);
  }
);

// 存储管理 API
export const storageApi = {
  // 获取存储统计（包含清理警告信息）
  getStats: () => api.get('/api/v1/storage/stats'),

  // 检查上传配额
  checkQuota: (fileSize: number, fileCount: number = 1) =>
    api.post('/api/v1/storage/check-quota', { fileSize, fileCount }),

  // 获取存储使用详情
  getDetails: () => api.get('/api/v1/storage/details'),
};

// 文件管理 API
export const filesApi = {
  // 获取文件列表
  getFiles: (params: {
    page?: number;
    pageSize?: number;
    category?: string;
    keyword?: string;
    sortBy?: string;
    order?: 'asc' | 'desc';
  }) => api.get('/api/v1/files', { params }),
  
  // 初始化上传
  initUpload: (data: {
    fileName: string;
    fileSize: number;
    fileType: string;
    chunkSize: number;
  }) => api.post('/api/v1/files/upload/init', data),
  
  // 上传分片（支持 AbortController）
  uploadPart: (
    key: string,
    uploadId: string,
    partNumber: number,
    data: ArrayBuffer,
    signal?: AbortSignal
  ) => api.post(`/api/v1/files/upload/part?key=${key}&uploadId=${uploadId}&partNumber=${partNumber}`, data, {
    headers: { 'Content-Type': 'application/octet-stream' },
    signal,
  }),
  
  // 完成上传
  completeUpload: (data: {
    uploadId: string;
    key: string;
    fileName: string;
    fileType: string;
    fileSize: number;
    parts: { partNumber: number; etag: string }[];
  }) => api.post('/api/v1/files/upload/complete', data),
  
  // 中止上传
  abortUpload: (data: {
    key: string;
    uploadId: string;
    fileSize: number;
  }) => api.post('/api/v1/files/upload/abort', data),
  
  // 删除文件
  deleteFile: (fileId: string) => api.delete(`/api/v1/files/${fileId}`),

  // 批量删除文件
  batchDeleteFiles: (fileIds: string[]) => api.post('/api/v1/files/batch-delete', { fileIds }),

  // 下载文件
  downloadFile: (fileId: string) => api.get(`/api/v1/files/${fileId}/download`, {
    responseType: 'blob',
  }),
};

// 认证 API
export const authApi = {
  // 检测安装状态
  checkInstallation: (handle: string) => 
    api.post('/api/v1/auth/check-installation', { handle }),
};

// 产品/套餐 API
export const productsApi = {
  // 获取产品列表
  getProducts: () => api.get('/products/list'),
  
  // 创建订阅订单
  createSubscription: (skuId: string, purchaseType: string) => 
    api.post('/api/v1/subscriptions/create', { skuId, purchaseType }),
};

export default api;